import {
  faEye,
  faLock,
  faPencil,
  faPlus,
  faTrashCan,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useContext, useEffect, useState } from "react";
import { networkService } from "../../../../../../config/axios";
import { EditorInfoContext } from "../../../../../../../utils/userInfoContext";
import CustomTooltip from "../../../../../../Elements/Tooltip";
import ViewLightBox from "./ViewLightBox";
import { TRIGGERS, triggerBadge } from "./LightBoxTrigger";
import { usageText } from "./LightBoxUsages";

const contentType = (lightbox) => {
  const types = [...new Set(Object.values(lightbox?.elements ?? {}).map((el) => el?.element_type_name).filter(Boolean))];
  return types.length ? types.join(", ") : "No content";
};

const timing = (lightbox) => {
  if (lightbox?.display !== "page") return null;
  const secs = lightbox?.show_after ? `${lightbox.show_after}s` : "0s";
  const page = lightbox?.show_on_page && lightbox.show_on_page !== "0" ? `p.${lightbox.show_on_page}` : "any page";
  return `${secs} on ${page}`;
};

const LightboxListing = ({
  docFileId,
  setKey,
  setEditLightBox,
  resetLightboxState,
}) => {
  const { setOpenRemoveModal, lightBoxList, setLightBoxList, setDeletingLightbox } =
    useContext(EditorInfoContext);

  const [loading, setLoading] = useState(false);
  const [showLightBox, setShowLightBox] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const fetchLightBoxList = async () => {
      setLoading(true);
      try {
        let list = await networkService(
          `docs/all-lightboxes/${docFileId}.json?by_trigger=no`,
          "GET"
        );
        const arrayOfLightbox = Object.values(
          Object.entries(list?.data).length !== 0 && list?.data
        );
        setLightBoxList(arrayOfLightbox);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching lightbox list:", error);
        setLoading(false);
      }
    };
    fetchLightBoxList();
  }, []);

  const counts = lightBoxList.reduce((acc, lb) => ({ ...acc, [lb?.display]: (acc[lb?.display] || 0) + 1 }), {});
  const visible = filter === "all" ? lightBoxList : lightBoxList.filter((lb) => lb?.display === filter);

  return (
    <>
      <ViewLightBox
        setShowLightBox={setShowLightBox}
        showLightBox={showLightBox}
      />
      <div className="lb-list-toolbar">
        <div className="lb-chips">
          <button type="button" className={`lb-chip${filter === "all" ? " active" : ""}`} onClick={() => setFilter("all")}>
            All {lightBoxList.length}
          </button>
          {TRIGGERS.filter((t) => counts[t.value]).map((t) => (
            <button type="button" key={t.value} className={`lb-chip${filter === t.value ? " active" : ""}`} onClick={() => setFilter(t.value)}>
              {t.badge} {counts[t.value]}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="lb-new-btn"
          onClick={() => {
            resetLightboxState();
            setKey("setting");
          }}
        >
          <FontAwesomeIcon icon={faPlus} /> New lightbox
        </button>
      </div>
      <div className="content-box">
        <table className="info-table lb-list-table">
          <thead>
            <tr>
              <th>Lightbox</th>
              <th>Trigger</th>
              <th>Used in</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="4">
                  <div className="spinner-wrapper">
                    <div
                      className="spinner-grow text-secondary spinner-text"
                      role="status"
                    >
                      <span className="sr-only">Loading...</span>
                    </div>
                  </div>
                </td>
              </tr>
            ) : visible.length > 0 ? (
              visible.map((lightbox, idx) => {
                const usages = lightbox?.usages ?? [];
                return (
                  <tr key={lightbox?.id ?? idx}>
                    <td>
                      <div className="lb-row-name">{lightbox?.name ?? "---"}</div>
                      <div className="lb-row-meta">
                        {contentType(lightbox)} · {lightbox?.width} × {lightbox?.height}
                        {lightbox?.created_user ? ` · by ${lightbox.created_user}` : ""}
                      </div>
                    </td>
                    <td>
                      <span className={`lb-badge lb-badge-${lightbox?.display}`}>{triggerBadge(lightbox?.display)}</span>
                      {timing(lightbox) && <div className="lb-row-meta">{timing(lightbox)}</div>}
                    </td>
                    <td>
                      {usages.length > 0 ? (
                        <CustomTooltip title={<p className="p-1 text-light mb-0">{usages.map(usageText).join(" · ")}</p>}>
                          <span className="lb-usage-chip">
                            {usages.length === 1 ? usageText(usages[0]) : `${usages.length} places`}
                          </span>
                        </CustomTooltip>
                      ) : (
                        <span className="lb-row-meta">Not used</span>
                      )}
                    </td>
                    <td>
                      <div className="lb-row-actions">
                        <CustomTooltip title={<p className="p-1 text-light mb-0">Preview</p>}>
                          <button type="button" className="lb-icon-btn" aria-label="Preview" onClick={() => setShowLightBox(lightbox)}>
                            <FontAwesomeIcon icon={faEye} />
                          </button>
                        </CustomTooltip>
                        <CustomTooltip title={<p className="p-1 text-light mb-0">Edit</p>}>
                          <button
                            type="button"
                            className="lb-icon-btn"
                            aria-label="Edit"
                            onClick={() => {
                              setEditLightBox(lightbox);
                              setKey("setting");
                            }}
                          >
                            <FontAwesomeIcon icon={faPencil} />
                          </button>
                        </CustomTooltip>
                        {usages.length > 0 ? (
                          <CustomTooltip
                            title={
                              <p className="p-1 text-light mb-0">
                                Can't delete yet. It is used by {usages.map(usageText).join(", ")}. Remove it from there first.
                              </p>
                            }
                          >
                            <span className="lb-icon-btn locked" aria-label="Delete unavailable, lightbox in use">
                              <FontAwesomeIcon icon={faLock} />
                            </span>
                          </CustomTooltip>
                        ) : (
                          <CustomTooltip title={<p className="p-1 text-light mb-0">Delete</p>}>
                            <button
                              type="button"
                              className="lb-icon-btn delete"
                              aria-label="Delete"
                              onClick={() => {
                                setEditLightBox(lightbox);
                                setDeletingLightbox(true);
                                setOpenRemoveModal(true);
                              }}
                            >
                              <FontAwesomeIcon icon={faTrashCan} />
                            </button>
                          </CustomTooltip>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td className="lightbx-msgg" colSpan="4">
                  <p>
                    {lightBoxList.length > 0
                      ? "No lightboxes with this trigger."
                      : "No lightboxes yet. Click New lightbox to add one."}
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="republish-banner lb-list-banner">
        After saving, republish this KDF for recipients to experience your changes.
      </div>
    </>
  );
};

export default LightboxListing;
